'use strict';
/*
  draw.browser.js — a browser port of lib/draw.js's toolkit.

  The Node toolkit uses node-canvas (createCanvas/registerFont) and macOS system
  font files. In the browser we use real <canvas> elements and web fonts loaded
  via CSS (see index.html). registerFamily() therefore just maps the engine's
  requested family name onto the matching loaded web-font family and returns it.

  The esbuild config aliases every `require('../lib/draw')` to THIS file, so the
  unmodified style modules + _common.js run unchanged in the browser.
*/

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
const easeOutBack = p => { const c1 = 1.70158, c3 = c1 + 1, x = p - 1; return 1 + c3 * x * x * x + c1 * x * x; };
const easeOutCubic = p => 1 - Math.pow(1 - p, 3);

function rng(seed) {
  let a = seed >>> 0;
  return () => { a |= 0; a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

function roundRect(c, x, y, w, h, r) {
  r = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
  c.beginPath(); c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r); c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r); c.arcTo(x, y, x + w, y, r); c.closePath();
}

const createCanvas = (w, h) => { const c = document.createElement('canvas'); c.width = Math.max(1, w | 0); c.height = Math.max(1, h | 0); return c; };

// engine font family name -> a loaded web-font family (see <link> in index.html)
function mapFamily(name) {
  const n = String(name || '');
  if (/pop/i.test(n)) return 'Anton';
  if (/mono/i.test(n)) return 'Special Elite';
  if (/din/i.test(n)) return 'Oswald';
  if (/futura/i.test(n)) return 'Jost';
  return 'Archivo Black'; // arialBlack-class default (chrome/kraft/brat/linger/aekko/sans/vhs)
}
// module-level registry, mirrors the Node toolkit's behaviour/signature
const _families = {};
function registerFamily(name, _paths, _weight) {
  if (_families[name] !== undefined) return _families[name];
  const fam = mapFamily(name); _families[name] = fam; return fam;
}

const FF = 'Arial, Helvetica, sans-serif';

function createToolkit(W, H) {
  const font = (px, weight = 900, family) => weight + ' ' + px + 'px ' + (family ? '"' + family + '", ' : '') + FF;
  const glyphCache = {}, tokenCache = {};

  function glyph(ch, px, color) {
    const key = ch + '|' + px + '|' + color;
    if (glyphCache[key]) return glyphCache[key];
    const size = Math.ceil(px * 1.8), cc = createCanvas(size, size), g = cc.getContext('2d');
    g.fillStyle = color; g.font = font(px); g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(ch, size / 2, size / 2);
    const o = { img: cc, half: size / 2 }; glyphCache[key] = o; return o;
  }

  function token(text, px, kindKey, paint) {
    const key = text + '|' + px + '|' + kindKey;
    if (tokenCache[key]) return tokenCache[key];
    let m = createCanvas(8, 8), g = m.getContext('2d'); g.font = font(px);
    const w = Math.ceil(g.measureText(text).width);
    const padX = Math.ceil(px * 0.20), hh = Math.ceil(px * 1.30);
    m = createCanvas(w + padX * 2 + 12, hh + 12); g = m.getContext('2d');
    g.font = font(px); g.textAlign = 'center'; g.textBaseline = 'middle';
    paint(g, text, m.width / 2, m.height / 2, px, w);
    const o = { img: m, hw: m.width / 2, hh: m.height / 2, w }; tokenCache[key] = o; return o;
  }

  const layers = {};
  function vignette(strength = 0.9, inner = 300) {
    const k = 'vig' + strength + inner; if (layers[k]) return layers[k];
    const cc = createCanvas(W, H), v = cc.getContext('2d');
    const g = v.createRadialGradient(W / 2, H / 2, inner, W / 2, H / 2, Math.max(W, H) * 0.78);
    g.addColorStop(0, 'rgba(0,0,0,0)'); g.addColorStop(1, 'rgba(0,0,0,' + strength + ')');
    v.fillStyle = g; v.fillRect(0, 0, W, H);
    layers[k] = cc; return cc;
  }
  function grain() {
    if (layers.grain) return layers.grain;
    const cc = createCanvas(W + 120, H + 120), gx = cc.getContext('2d');
    const id = gx.createImageData(cc.width, cc.height);
    for (let i = 0; i < id.data.length; i += 4) { const n = Math.random() * 255; id.data[i] = id.data[i + 1] = id.data[i + 2] = n; id.data[i + 3] = 255; }
    gx.putImageData(id, 0, 0); layers.grain = cc; return cc;
  }
  function scanlines(alpha = 0.55, gap = 3) {
    const k = 'scan' + alpha + gap; if (layers[k]) return layers[k];
    const sl = createCanvas(2, gap), s = sl.getContext('2d');
    s.fillStyle = 'rgba(0,0,0,' + alpha + ')'; s.fillRect(0, gap - 1, 2, 1);
    const cc = createCanvas(W, H), c = cc.getContext('2d');
    c.fillStyle = c.createPattern(sl, 'repeat'); c.fillRect(0, 0, W, H);
    layers[k] = cc; return cc;
  }
  function chip(ctx, T, px, padXf = 0.16, padYf = 0.20, r) {
    const m = ctx.measureText(T);
    let asc = m.actualBoundingBoxAscent, desc = m.actualBoundingBoxDescent;
    let L = m.actualBoundingBoxLeft, R = m.actualBoundingBoxRight;
    if (asc == null || !isFinite(asc)) { asc = px * 0.36; desc = px * 0.10; }
    if (L == null || !isFinite(L)) { L = m.width / 2; R = m.width / 2; }
    const padX = px * padXf, padY = px * padYf;
    roundRect(ctx, -L - padX, -asc - padY, L + R + padX * 2, asc + desc + padY * 2, r == null ? px * 0.18 : r);
  }
  let _small, _smallCtx;
  function bloom(dst, src, amount = 0.9, passes = 2, div = 7) {
    const dw = Math.max(1, Math.round(W / div)), dh = Math.max(1, Math.round(H / div));
    if (!_small || _small.width !== dw || _small.height !== dh) { _small = createCanvas(dw, dh); _smallCtx = _small.getContext('2d'); }
    _smallCtx.clearRect(0, 0, dw, dh); _smallCtx.imageSmoothingEnabled = true;
    _smallCtx.drawImage(src, 0, 0, dw, dh);
    dst.save(); dst.globalCompositeOperation = 'screen'; dst.imageSmoothingEnabled = true;
    for (let i = 0; i < passes; i++) { dst.globalAlpha = amount * (1 - i * 0.25); dst.drawImage(_small, 0, 0, W, H); }
    dst.restore();
  }

  return { W, H, FF, font, registerFamily, glyph, token, chip, roundRect, vignette, grain, scanlines, bloom, createCanvas };
}

// Same keys the styles request; values are irrelevant in the browser (registerFamily
// maps by NAME), but kept so `FONTS.arialBlack` etc. resolve to a truthy value.
const FONTS = {
  impact: ['Anton'], dinCond: ['Oswald'], futura: ['Jost'],
  arialBlack: ['Archivo Black'], phosphate: ['Anton'], mono: ['Special Elite'],
};

export { createToolkit, registerFamily, FONTS, rng, clamp, lerp, easeOutBack, easeOutCubic, roundRect };
